<script setup>
import { useI18n } from 'vue-i18n';
import { isUuid } from 'dashboard/composables/usePiiRedaction';

defineProps({
  label: { type: String, default: '' },
  value: { type: null, default: null },
  // Raw key this value was rendered under in its parent object, e.g. "otps" or
  // "signatures". Used to detect verification arrays that must never show raw
  // OTP codes / signature images — see SENSITIVE_KEYS / VERIFICATION_ARRAYS below.
  sourceKey: { type: String, default: '' },
});

defineOptions({ name: 'ProcessDetailField' });

const { t } = useI18n();

// Fields that must never render their raw value (raw OTP codes, base64 signature
// images) regardless of which process or where in the response tree they appear.
const SENSITIVE_KEYS = ['signature', 'profile_signature', 'code'];

// Arrays of per-person verification records, keyed by which boolean field to
// collapse each entry down to. Naming varies by process (otps vs otp).
const VERIFICATION_ARRAYS = {
  otps: 'verified',
  otp: 'verified',
  signatures: 'signed',
};

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isPrimitive(v) {
  return v === null || v === undefined || typeof v !== 'object';
}

function humanize(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function displayValue(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function personName(item) {
  return (
    item.user_details?.names ||
    item.user_details?.name ||
    item.full_name ||
    item.name ||
    'Unknown'
  );
}
</script>

<template>
  <div>
    <template v-if="Array.isArray(value) && VERIFICATION_ARRAYS[sourceKey]">
      <p v-if="label" class="mb-1 text-xs font-semibold text-gray-500">
        {{ label }}
      </p>
      <div v-if="!value.length" class="mb-2 text-sm text-gray-400">—</div>
      <div
        v-for="(item, index) in value"
        :key="index"
        class="mb-1 flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
      >
        <span class="text-sm text-gray-900">{{ personName(item) }}</span>
        <span
          class="text-xs font-medium px-2 py-0.5 rounded-full"
          :class="
            item[VERIFICATION_ARRAYS[sourceKey]]
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600'
          "
        >
          {{
            item[VERIFICATION_ARRAYS[sourceKey]]
              ? t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.VERIFIED')
              : t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.NOT_VERIFIED')
          }}
        </span>
      </div>
    </template>
    <template v-else-if="Array.isArray(value)">
      <p v-if="label" class="mb-1 text-xs font-semibold text-gray-500">
        {{ label }}
      </p>
      <div v-if="!value.length" class="mb-2 text-sm text-gray-400">—</div>
      <div
        v-for="(item, index) in value"
        :key="index"
        class="mb-2 ml-2 rounded-lg border border-gray-200 p-3"
      >
        <ProcessDetailField v-if="!isPrimitive(item)" :value="item" />
        <p v-else-if="!isUuid(item)" class="text-sm">
          {{ displayValue(item) }}
        </p>
      </div>
    </template>
    <template v-else-if="isPlainObject(value)">
      <div class="space-y-2">
        <div v-for="(v, k) in value" :key="k">
          <template v-if="isPrimitive(v) && isUuid(v)" />
          <template v-else-if="SENSITIVE_KEYS.includes(String(k))">
            <p class="text-xs text-gray-500">{{ humanize(String(k)) }}</p>
            <p class="mb-1 text-sm text-gray-400">
              {{ t('ARDHISASA.LAND_PROCESSES.LAND_REGISTRATION.HIDDEN') }}
            </p>
          </template>
          <template v-else-if="isPrimitive(v)">
            <p class="text-xs text-gray-500">{{ humanize(String(k)) }}</p>
            <p class="mb-1 text-sm text-gray-900">{{ displayValue(v) }}</p>
          </template>
          <template v-else>
            <p class="mb-1 mt-2 text-xs font-semibold text-gray-500">
              {{ humanize(String(k)) }}
            </p>
            <div class="ml-2 border-l-2 border-gray-100 pl-3">
              <ProcessDetailField :value="v" :source-key="String(k)" />
            </div>
          </template>
        </div>
      </div>
    </template>
    <template v-else-if="!isUuid(value)">
      <p class="text-sm text-gray-900">{{ displayValue(value) }}</p>
    </template>
  </div>
</template>
